import { useState, useRef, useEffect } from "react";
import http from "../../../utils/SupabaseHttp";
import DashboardLayout from "../../../components/dashboard/DashboardLayout";
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import Button from "../../../components/shared/Button";
import { Delete } from "@mui/icons-material";
import HowToRecordModal from "../../../components/shared/HowToRecordModal";
import { toast } from "react-toastify";
import { updateToast } from "../../../utils/toast_notification";
import { useTranscriptionStripeCardDetails } from "../../../hooks/useTranscriptionStripeCardDetails";
import {
  Elements,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import TranscriptionPaymentForm from "../../../components/transcription/TranscriptionPaymentForm";
import { TranscriptionCostModal } from "../../../components/transcription/TranscriptionCostModal";
import { useTranscriptionSocket } from "../../../hooks/useTranscriptionSocket";
import { Modal, Box, Typography, CircularProgress } from "@mui/material";

interface Recording {
  id: string;
  title: string;
  audioURL: string;
  audioBlob: Blob;
  duration: number;
  timestamp: Date;
}

const AudioRecorder = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [audioURL, setAudioURL] = useState<string>("");
  const [recordingTime, setRecordingTime] = useState(0);
  const [error, setError] = useState<string>("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<any>(null);
  const [isCurrentRecordingUploaded, setIsCurrentRecordingUploaded] = useState(false);
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [recordingTitle, setRecordingTitle] = useState<string>("");
  const [showNewRecordingForm, setShowNewRecordingForm] = useState<boolean>(false);
  const [showHowToModal, setShowHowToModal] = useState<boolean>(false);
  const [hasAddedCard, setHasAddedCard] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);

  // Initialize Stripe
  const stripePromise = loadStripe(
    process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY as string
  );

  // Check for saved cards using the transcription hook
  const { isChecking, hasSavedCard } = useTranscriptionStripeCardDetails({
    setHasAddedCard,
  });

  // WebSocket hook for transcription completion
  const { transcriptionCompleted, clearTranscriptionCompleted } = useTranscriptionSocket();

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioBlobRef = useRef<Blob | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  // Visualizer refs
  const micStreamRef = useRef<MediaStream | null>(null);
  const systemStreamRef = useRef<MediaStream | null>(null);
  const micAnalyserRef = useRef<AnalyserNode | null>(null);
  const systemAnalyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const micCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const systemCanvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    // Check if user has seen the modal before
    const hasSeenModal = localStorage.getItem('hasSeenHowToRecordModal');
    if (!hasSeenModal) {
      setShowHowToModal(true);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      // cancel animation
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  const startRecording = async () => {
    // Check if user has saved card
    if (!hasSavedCard) {
      // Show payment modal first
      setIsPaymentModalOpen(true);
      return;
    }

    proceedWithRecording();
  };

  const proceedWithRecording = async () => {
    setIsCurrentRecordingUploaded(false);
    const loadingToastId = toast.loading("Starting recording...");
    
    try {
      setError("");
      audioChunksRef.current = [];

      // Get microphone audio
      const micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        }
      });

      // Try to get system audio (screen capture with audio)
      let systemStream: MediaStream | null = null;
      try {
        systemStream = await (navigator as any).mediaDevices.getDisplayMedia({
          video: false,
          audio: true
        });
      } catch (systemAudioError) {
        console.warn("System audio not available:", systemAudioError);
      }

      // Create audio context to mix streams
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      audioContextRef.current = audioContext;

      // Combine microphone and system audio
      const combinedStream = new MediaStream();

      // Add microphone tracks
      micStream.getAudioTracks().forEach(track => {
        combinedStream.addTrack(track);
      });

      // Add system audio tracks if available
      if (systemStream) {
        systemStream.getAudioTracks().forEach(track => {
          combinedStream.addTrack(track);
        });
      }

      streamRef.current = combinedStream;

      // store original streams for visualization
      micStreamRef.current = micStream;
      systemStreamRef.current = systemStream;

      // Setup visualizers for separate streams
      try {
        // create analyser for mic
        const micSource = audioContext.createMediaStreamSource(micStream);
        const micAnalyser = audioContext.createAnalyser();
        micAnalyser.fftSize = 2048;
        micSource.connect(micAnalyser);
        micAnalyserRef.current = micAnalyser;

        // create analyser for system audio if available
        if (systemStream) {
          const systemSource = audioContext.createMediaStreamSource(systemStream);
          const systemAnalyser = audioContext.createAnalyser();
          systemAnalyser.fftSize = 2048;
          systemSource.connect(systemAnalyser);
          systemAnalyserRef.current = systemAnalyser;
        }

        // start the draw loop
        startVisualizer();
      } catch (visErr) {
        console.warn('Visualizer setup failed', visErr);
      }

      // Create media recorder
      const mediaRecorder = new MediaRecorder(combinedStream, {
        mimeType: 'audio/webm;codecs=opus'
      });

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        audioBlobRef.current = audioBlob;
        const url = URL.createObjectURL(audioBlob);
        setAudioURL(url);
        setShowNewRecordingForm(true);
        
        // Show success toast for recording complete
        toast.success("Recording completed successfully!");
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();

      setIsRecording(true);
      setIsPaused(false);
      setRecordingTime(0);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

      // Show success toast for starting recording
      updateToast(loadingToastId, "Recording started successfully!", "success");

    } catch (err) {
      setError("Failed to start recording. Please check microphone permissions.");
      console.error("Recording error:", err);
      
      // Show error toast
      updateToast(loadingToastId, "Failed to start recording. Please check microphone permissions.", "error");
    }
  };

  const startVisualizer = () => {
    const micCanvas = micCanvasRef.current;
    const systemCanvas = systemCanvasRef.current;
    const micAnalyser = micAnalyserRef.current;
    const systemAnalyser = systemAnalyserRef.current;

    if (!micCanvas || !micAnalyser) return;

    const micCtx = micCanvas.getContext('2d');
    const systemCtx = systemCanvas?.getContext('2d') || null;

    const bufferLength = micAnalyser.frequencyBinCount;
    const micDataArray = new Uint8Array(bufferLength);
    const systemDataArray = systemAnalyser ? new Uint8Array(systemAnalyser.frequencyBinCount) : null;

    const draw = () => {
      animationFrameRef.current = requestAnimationFrame(draw);

      // draw mic waveform
      micAnalyser.getByteTimeDomainData(micDataArray);
      if (micCtx) {
        micCtx.fillStyle = '#f8fafc';
        micCtx.fillRect(0, 0, micCanvas.width, micCanvas.height);

        micCtx.lineWidth = 2;
        micCtx.strokeStyle = '#10b981';
        micCtx.beginPath();

        const sliceWidth = micCanvas.width / bufferLength;
        let x = 0;
        for (let i = 0; i < bufferLength; i++) {
          const v = micDataArray[i] / 128.0;
          const y = (v * micCanvas.height) / 2;
          if (i === 0) {
            micCtx.moveTo(x, y);
          } else {
            micCtx.lineTo(x, y);
          }
          x += sliceWidth;
        }
        micCtx.lineTo(micCanvas.width, micCanvas.height / 2);
        micCtx.stroke();
      }

      // draw system waveform if available
      if (systemAnalyser && systemCtx && systemDataArray && systemCanvas) {
        systemAnalyser.getByteTimeDomainData(systemDataArray);
        systemCtx.fillStyle = '#f8fafc';
        systemCtx.fillRect(0, 0, systemCanvas.width, systemCanvas.height);

        systemCtx.lineWidth = 2;
        systemCtx.strokeStyle = '#3b82f6';
        systemCtx.beginPath();

        const sliceWidthS = systemCanvas.width / systemDataArray.length;
        let xS = 0;
        for (let i = 0; i < systemDataArray.length; i++) {
          const v = systemDataArray[i] / 128.0;
          const y = (v * systemCanvas.height) / 2;
          if (i === 0) {
            systemCtx.moveTo(xS, y);
          } else {
            systemCtx.lineTo(xS, y);
          }
          xS += sliceWidthS;
        }
        systemCtx.lineTo(systemCanvas.width, systemCanvas.height / 2);
        systemCtx.stroke();
      }
    };

    // size canvases to device pixel ratio for sharpness
    const resizeCanvas = (canvas: HTMLCanvasElement) => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.scale(dpr, dpr);
    };

    resizeCanvas(micCanvas);
    if (systemCanvas) resizeCanvas(systemCanvas);

    draw();
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);

      if (timerRef.current) {
        clearInterval(timerRef.current);
      }

      // Stop all tracks
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }

      // Stop visualizer animation and disconnect analysers
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (micAnalyserRef.current) {
        try { micAnalyserRef.current.disconnect(); } catch (e) { }
        micAnalyserRef.current = null;
      }
      if (systemAnalyserRef.current) {
        try { systemAnalyserRef.current.disconnect(); } catch (e) { }
        systemAnalyserRef.current = null;
      }
      micStreamRef.current = null;
      systemStreamRef.current = null;
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && isRecording && !isPaused) {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && isRecording && isPaused) {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    }
  };

  const downloadAudio = () => {
    if (audioURL) {
      const a = document.createElement('a');
      a.href = audioURL;
      a.download = `recording-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.webm`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  const uploadAudio = async (recording?: Recording) => {
    // Check if user has saved card
    if (!hasSavedCard) {
      // Show payment modal first
      setIsPaymentModalOpen(true);
      return;
    }

    proceedWithUpload(recording);
  };

  const proceedWithUpload = async (recording?: Recording) => {
    const audioBlob = recording?.audioBlob || audioBlobRef.current;
    const title = recording?.title || recordingTitle || 'Untitled Recording';
    
    if (!audioBlob) {
      setError("No audio recording available to upload");
      toast.error("No audio recording available to upload");
      return;
    }

    const loadingToastId = toast.loading("Uploading recorded audio...");
    setIsUploading(true);
    setError("");
    setUploadResult(null);

    try {
      const formData = new FormData();
      const fileName = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.webm`;
      formData.append('audio', audioBlob, fileName);
      formData.append('title', title);
      formData.append('duration', (recording?.duration || recordingTime).toString());
      formData.append('timestamp', (recording?.timestamp || new Date()).toISOString());

      const response = await http.post('/transcription/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setUploadResult(response.data);
      setIsCurrentRecordingUploaded(true);
      
      // Show success toast for upload complete
      updateToast(loadingToastId, "Kindly sit back as we work on your documentation", "success");
      
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to upload audio. Please try again.");
      console.error("Upload error:", err);
      
      // Show error toast for upload failure
      updateToast(loadingToastId, err.response?.data?.error || "Failed to upload audio. Please try again.", "error");
    } finally {
      setIsUploading(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} mins ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    return `${Math.floor(diffInSeconds / 86400)} days ago`;
  };

  const saveRecording = () => {
    if (!audioBlobRef.current || !audioURL) return;
    
    const newRecording: Recording = {
      id: Date.now().toString(),
      title: recordingTitle || 'Untitled Recording',
      audioURL,
      audioBlob: audioBlobRef.current,
      duration: recordingTime,
      timestamp: new Date()
    };
    
    setRecordings(prev => [newRecording, ...prev]);
    setRecordingTitle('');
    setAudioURL('');
    setShowNewRecordingForm(false);
    setRecordingTime(0);
  };

  const deleteRecording = (id: string) => {
    setRecordings(prev => prev.filter(rec => rec.id !== id));
  };

  const downloadRecording = (recording: Recording) => {
    const a = document.createElement('a');
    a.href = recording.audioURL;
    a.download = `${recording.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.webm`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleDoNotShowAgain = (doNotShow: boolean) => {
    if (doNotShow) {
      localStorage.setItem('hasSeenHowToRecordModal', 'true');
    }
  };

  const fetchSetupIntent = async () => {
    try {
      const response = await http.post("/transcription/create-setup-intent");
      
      if (response.status === 200 && response?.data?.clientSecret) {
        setClientSecret(response?.data?.clientSecret);
      } else {
        throw new Error("Failed to initialize payment setup");
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error || "Unable to initialize payment setup."
      );
      setIsPaymentModalOpen(false);
    }
  };

  useEffect(() => {
    if (isPaymentModalOpen) {
      fetchSetupIntent();
    }
  }, [isPaymentModalOpen]);

  const handlePaymentSuccess = () => {
    setHasAddedCard(true);
    // Proceed with the intended action after successful card addition
    if (!isRecording && !audioURL) {
      // User was trying to start recording
      proceedWithRecording();
    } else if (audioURL && !isCurrentRecordingUploaded) {
      // User was trying to upload
      proceedWithUpload();
    }
  };

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-[90%] mx-auto">
          {/* Recording List Section */}
          <div className="">
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Meeting Transcribe</h2>
              <small className="text-center block mb-6">Record your appointment now</small>

              {/* Visualizers: Microphone and System Audio */}
              <div className="mb-6">
                <div className="w-full h-24 rounded overflow-hidden">
                  <canvas ref={micCanvasRef} className="w-full h-full"></canvas>
                </div>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                  {error}
                </div>
              )}

              <div className="text-center mb-6">
                {isRecording ? (
                  <div className="text-3xl font-mono text-gray-700">
                    {formatTime(recordingTime)}
                  </div>
                ) : (
                  <div>Click on the button to record now</div>
                )}
                {isRecording && (
                  <div className="mt-2">
                    <span className={`inline-block w-3 h-3 rounded-full ${isPaused ? 'bg-yellow-500' : 'bg-red-500 animate-pulse'}`}></span>
                    <span className="ml-2 text-sm text-gray-600">
                      {isPaused ? 'Paused' : 'Recording...'}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex justify-center space-x-4 mb-6">
                {!isRecording ? (
                  <button
                    onClick={startRecording}
                    disabled={isChecking}
                    className="p-8 hover:bg-[#324705] text-white rounded-full bg-green-800 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isChecking ? (
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                    ) : (
                      <GraphicEqIcon fontSize="large" />
                    )}
                  </button>
                ) : (
                  <>
                    {!isPaused ? (
                      <button
                        onClick={pauseRecording}
                        className="px-6 py-3 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors font-medium"
                      >
                        Pause
                      </button>
                    ) : (
                      <button
                        onClick={resumeRecording}
                        className="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium"
                      >
                        Resume
                      </button>
                    )}
                    <button
                      onClick={stopRecording}
                      className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
                    >
                      Stop
                    </button>
                  </>
                )}
              </div>

              {audioURL && (
                <div className={`border-t w-full bg-slate-200 px-5 py-5 rounded-xl ${isCurrentRecordingUploaded ? 'opacity-50' : ''}`}>
                  <h3 className="text-lg font-semibold text-primary mb-4">Recording Complete</h3>
                  
                  <div className="space-y-4 w-full flex gap-5  justify-between items-center">
                    <div className="w-full">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Add a caption for this meeting
                      </label>
                      <input
                        type="text"
                        value={recordingTitle}
                        onChange={(e) => setRecordingTitle(e.target.value)}
                        placeholder="Add Meeting title or caption"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={isCurrentRecordingUploaded}
                      />
                    </div>

                    <audio controls className="w-full mb-4" src={audioURL}>
                      Your browser does not support the audio element.
                    </audio>

                    {/* Recording Details
                    <div className="flex gap-4 text-sm text-gray-600">
                      <span> {formatTime(recordingTime)}</span>
                    </div> */}

                    <div className="flex gap-5">
                      {/* <button
                        onClick={saveRecording}
                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                      >
                        Save Recording
                      </button> */}
                      <button
                        onClick={() => {
                          setAudioURL('');
                          setRecordingTitle('');
                          setRecordingTime(0);
                          setIsCurrentRecordingUploaded(false);
                        }}
                        className="flex-1 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
                      >
                        <Delete />
                      </button>
                      <Button
                        onClick={() => uploadAudio()}
                        variant="primary"
                        disabled={isUploading || isCurrentRecordingUploaded || isChecking}
                        label={isCurrentRecordingUploaded ? 'Generated' : isUploading ? 'Generating...' : isChecking ? 'Checking...' : 'Generate'}
                        // className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
                      />
                        
                      
                    </div>
                  </div>

                  {/* {uploadResult && (
                    <div className="mt-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
                      <p className="font-medium">Upload Successful!</p>
                      <p className="text-sm">Job ID: {uploadResult.jobId}</p>
                      <p className="text-sm">Status: {uploadResult.status}</p>
                    </div>
                  )} */}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Payment Modal */}
      <Modal
        open={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        aria-labelledby="payment-modal-title"
        aria-describedby="payment-modal-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: 500,
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 2,
          p: 4,
          maxHeight: '90vh',
          overflow: 'auto'
        }}>
          <Typography id="payment-modal-title" variant="h6" component="h2" gutterBottom>
            Add Payment Method
          </Typography>
          <Typography id="payment-modal-description" variant="body2" color="textSecondary" paragraph>
            We need a payment method on file to process your transcription. You will be charged per transcription completed.
          </Typography>
          
          {clientSecret ? (
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret,
                appearance: {
                  theme: "stripe",
                  variables: {
                    colorPrimary: "#3d874e",
                    colorBackground: "#ffffff",
                    colorText: "#2e3011",
                    fontFamily: "Inter, system-ui, sans-serif",
                  },
                },
              }}
            >
              <TranscriptionPaymentForm
                clientSecret={clientSecret}
                onSuccess={handlePaymentSuccess}
                onClose={() => setIsPaymentModalOpen(false)}
              />
            </Elements>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          )}
        </Box>
      </Modal>
      
      {/* Transcription Cost Modal */}
      <TranscriptionCostModal
        isVisible={!!transcriptionCompleted}
        onClose={clearTranscriptionCompleted}
        transcriptionData={transcriptionCompleted}
      />
      
      <HowToRecordModal
        isOpen={showHowToModal}
        onClose={() => setShowHowToModal(false)}
        onDoNotShowAgain={handleDoNotShowAgain}
      />
    </DashboardLayout>
  );
};

export default AudioRecorder;